// src/utils/cssSwitcher.js
//
// Swaps Framework7's LTR/RTL stylesheet at runtime to match the active language.
//
// The two URLs MUST come from the bundler, never from a hand-written path.
// `link.href` resolves against `document.baseURI` — the CURRENT URL — so a
// relative path like '../css/framework7-bundle-rtl.min.css' silently resolves
// differently depending on how deep the loaded route is:
//
//     /orders/            → /css/framework7-bundle-rtl.min.css        ✅
//     /issues/{id}/       → /issues/css/framework7-bundle-rtl.min.css ❌
//     /orders/box/{id}/…  → /orders/box/{id}/css/…                    ❌
//
// The miss is invisible: the SPA fallback answers those paths with 200 +
// index.html, so the browser discards a stylesheet served as text/html without
// logging anything, and EVERY Framework7 rule vanishes. Only cold loads at
// depth are affected (in-app navigation keeps whatever resolved first), which
// is exactly the push-deep-link and PWA-start case — see
// `_bmad-output/audits/2026-08-10-collection-issue-browser-pass.md` (F1).
//
// Importing through the package's own export subpaths also keeps the CSS
// version-locked to the installed `framework7` release, instead of the stale
// hand-copied files that used to live in `public/css/` (F10).
import rtlCssUrl from 'framework7/css/bundle/rtl?url';
import ltrCssUrl from 'framework7/css/bundle?url';

const CSS_IDS = {
  ltr: 'f7-ltr-css',
  rtl: 'f7-rtl-css',
};

// Bundler-resolved, absolute and fingerprinted. Never make these relative.
const CSS_PATHS = {
  ltr: ltrCssUrl,
  rtl: rtlCssUrl,
};

export function switchF7Css(isRTL) {
  // Remove both if present
  Object.values(CSS_IDS).forEach(id => {
    const el = document.getElementById(id);
    if (el) el.remove();
  });

  // Create and append the required CSS
  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.id = isRTL ? CSS_IDS.rtl : CSS_IDS.ltr;
  link.href = isRTL ? CSS_PATHS.rtl : CSS_PATHS.ltr;
  document.head.prepend(link);
  document.documentElement.lang = isRTL ? 'ar' : 'en';
  document.documentElement.dir = isRTL ? 'rtl' : 'ltr';
}
